import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'
import io from 'socket.io-client'

import { getToken, getUserId } from '../../services/authorize'
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api/notificationsApi'

function fmt(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diff = (now - d) / 1000 / 60
  if (diff < 1) return 'เมื่อสักครู่'
  if (diff < 60) return `${Math.floor(diff)} นาทีที่แล้ว`
  if (diff < 60 * 24) return `${Math.floor(diff / 60)} ชั่วโมงที่แล้ว`
  return d.toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })
}

// mode: 'staff' | 'student' — picks which session to use
export default function NotificationBell({ mode = 'staff' }) {
  const navigate = useNavigate()
  const [count, setCount] = useState(0)
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)
  const socketRef = useRef(null)

  const currentRole = 'staff'
  const currentId = getUserId()
  const haveToken = !!getToken()

  const refreshCount = useCallback(() => {
    if (!haveToken) return
    getUnreadCount(mode).then(r => setCount(r.count || 0)).catch(() => {})
  }, [mode, haveToken])

  const refreshList = useCallback(() => {
    if (!haveToken) return
    listNotifications(mode, 30).then(setItems).catch(() => setItems([]))
  }, [mode, haveToken])

  useEffect(() => {
    if (!haveToken) return
    refreshCount()

    const token = getToken()
    const url = process.env.REACT_APP_SOCKET_URL
    if (!url) return
    const socket = io(url, { auth: { token } })
    socketRef.current = socket

    socket.on('notification:new', (payload) => {
      if (!payload) return
      if (payload.recipientRole !== currentRole) return
      if (payload.recipientId !== currentId) return
      setCount(c => c + 1)
      // If the dropdown is open, refresh the list inline so the new item appears
      if (open) refreshList()
    })

    return () => { socket.disconnect() }
    // eslint-disable-next-line
  }, [mode, haveToken, currentRole, currentId])

  const handleOpen = (next) => {
    setOpen(next)
    if (next) refreshList()
  }

  const handleClickItem = async (n) => {
    try {
      if (!n.noti_read_at) {
        await markNotificationRead(mode, n.noti_id)
        setCount(c => Math.max(0, c - 1))
      }
    } catch {/* noop */}
    setOpen(false)
    if (n.noti_link) {
      navigate(n.noti_link)
    }
  }

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead(mode)
      setCount(0)
      setItems(items.map(i => ({ ...i, noti_read_at: i.noti_read_at || new Date().toISOString() })))
    } catch {/* noop */}
  }

  if (!haveToken) return null

  return (
    <Dropdown
      className="dropdown-notification ms-3 ms-xl-4"
      align="end"
      show={open}
      onToggle={handleOpen}
    >
      <Dropdown.Toggle as="a" role="button" className="position-relative" style={{ cursor: 'pointer' }}>
        <i className="ri-notification-3-line fs-4" />
        {count > 0 && (
          <span
            className="position-absolute badge rounded-pill bg-danger"
            style={{ top: -4, right: -8, fontSize: 10 }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="mt-10-f" style={{ width: 340, maxWidth: '90vw' }}>
        <div className="dropdown-menu-header d-flex align-items-center justify-content-between">
          <h6 className="dropdown-menu-title mb-0">การแจ้งเตือน</h6>
          {count > 0 && (
            <button className="btn btn-link btn-sm p-0" onClick={handleMarkAll}>
              อ่านทั้งหมด
            </button>
          )}
        </div>

        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {items.length === 0
            ? <div className="text-center text-muted small py-4">ไม่มีการแจ้งเตือน</div>
            : items.map(n => (
                <button
                  key={n.noti_id}
                  type="button"
                  onClick={() => handleClickItem(n)}
                  className={'dropdown-item text-wrap py-2 ' + (n.noti_read_at ? '' : 'fw-semibold bg-light')}
                  style={{ whiteSpace: 'normal' }}
                >
                  <div className="d-flex gap-2">
                    <i className="ri-notification-2-line mt-1 text-primary" />
                    <div className="flex-grow-1">
                      <div>{n.noti_title}</div>
                      {n.noti_body && <div className="small text-muted">{n.noti_body}</div>}
                      <div className="small text-muted">{fmt(n.noti_created_at)}</div>
                    </div>
                  </div>
                </button>
              ))
          }
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
